import _extends from "@babel/runtime/helpers/extends";
import { LayerContextProvider } from "@smartsheet/lodestar-core";
import { SDSThemeProvider } from "@smartsheet/lodestar-core";
import { useForkRef, useRequestAnimationFrame, useResizeObserver } from "@smartsheet/lodestar-utils";
import React, { useCallback, useEffect, useState } from "react";
import { Toast, ToastActions, ToastContent, ToastDismissButton } from "..";
import { ToastContainerStyled } from "../styled/ToastContainerStyled";
import { ToastAlertContent } from "./ToastAlertContent";
const ToastContainerInnerElements = _ref => {
  let {
    entry,
    offset,
    sdsProviderProps,
    sdsProviderStyles,
    isVisible,
    combinedRef,
    pauseAutoCloseTimers,
    restartAutoCloseTimers,
    maybeCallElementReadyRef,
    triggerFadeOut,
    ...rest
  } = _ref;
  return /*#__PURE__*/React.createElement(React.Fragment, null, entry.themeKey ? /*#__PURE__*/React.createElement(SDSThemeProvider, _extends({
    themeKey: entry.themeKey,
    innerWrapperStyles: {
      position: "unset",
      width: "unset",
      height: "unset",
      display: "unset",
      ...sdsProviderStyles
    }
  }, sdsProviderProps), /*#__PURE__*/React.createElement(ToastContainerStyled, _extends({}, rest, {
    style: {
      transform: `translateY(${offset}px)`,
      opacity: isVisible ? 1 : 0
    },
    ref: combinedRef,
    onMouseEnter: pauseAutoCloseTimers,
    onMouseLeave: restartAutoCloseTimers
  }), entry.entryType === "simple" && /*#__PURE__*/React.createElement(Toast, {
    type: entry.type || "info",
    ref: maybeCallElementReadyRef
  }, /*#__PURE__*/React.createElement(ToastContent, null, /*#__PURE__*/React.createElement(ToastAlertContent, null, entry.message)), /*#__PURE__*/React.createElement(ToastActions, null, /*#__PURE__*/React.createElement(ToastDismissButton, {
    "aria-label": "Close notification",
    onClick: triggerFadeOut
  }))))) : /*#__PURE__*/React.createElement(ToastContainerStyled, _extends({}, rest, {
    style: {
      transform: `translateY(${offset}px)`,
      opacity: isVisible ? 1 : 0
    },
    ref: combinedRef,
    onMouseEnter: pauseAutoCloseTimers,
    onMouseLeave: restartAutoCloseTimers
  }), entry.entryType === "simple" && /*#__PURE__*/React.createElement(Toast, {
    type: entry.type || "info",
    ref: maybeCallElementReadyRef
  }, /*#__PURE__*/React.createElement(ToastContent, null, /*#__PURE__*/React.createElement(ToastAlertContent, null, entry.message)), /*#__PURE__*/React.createElement(ToastActions, null, /*#__PURE__*/React.createElement(ToastDismissButton, {
    "aria-label": "Close notification",
    onClick: triggerFadeOut
  })))));
};

/**
 * This is used to wrap and animate the Toast. The Toast controls what it looks like and lays out
 * normally. The container is used to position it properly based on its current animation status and
 * offset.
 */
export const ToastContainer = _ref2 => {
  let {
    entry,
    offset,
    updateHeight,
    triggerFadeOut,
    pauseAutoCloseTimers,
    restartAutoCloseTimers,
    sdsProviderProps,
    sdsProviderStyles,
    ...rest
  } = _ref2;
  // We have to set visibility after a tick or so to allow the element to be rendered into the DOM
  // so it can be animated.
  const [isVisible, setIsVisible] = useState(false);
  const {
    raf: resizeRaf
  } = useRequestAnimationFrame();
  const {
    ref: resizeRef
  } = useResizeObserver(useCallback(entries => {
    // Wrap in a RAF to ensure the resizeobserver can finish its notification cycle before
    // we try to update the height. This avoids this class of errors:
    // https://developer.mozilla.org/en-US/docs/Web/API/ResizeObserver#observation_errors
    resizeRaf(() => {
      updateHeight(entry.id, entries[0].target);
    });
  }, [resizeRaf, updateHeight, entry.id]));
  const maybeCallElementReadyRef = useCallback(element => {
    if (entry.entryType === "interactive" && element) {
      entry.onElementReady({
        element
      });
    }
  }, [entry]);
  const combinedRef = useForkRef(resizeRef, maybeCallElementReadyRef);
  const {
    raf: visibilityRaf
  } = useRequestAnimationFrame();
  useEffect(() => {
    if (entry.status === "entering" || entry.status === "entered") {
      visibilityRaf(() => {
        setIsVisible(true);
      });
    } else if (entry.status === "exiting") {
      setIsVisible(false);
    }
  }, [visibilityRaf, entry.status]);
  return /*#__PURE__*/React.createElement(LayerContextProvider, {
    defaultLayer: "toast"
  }, /*#__PURE__*/React.createElement(ToastContainerInnerElements, _extends({
    entry: entry,
    offset: offset,
    sdsProviderProps: sdsProviderProps,
    sdsProviderStyles: sdsProviderStyles,
    isVisible: isVisible,
    combinedRef: combinedRef,
    pauseAutoCloseTimers: pauseAutoCloseTimers,
    restartAutoCloseTimers: restartAutoCloseTimers,
    maybeCallElementReadyRef: maybeCallElementReadyRef,
    triggerFadeOut: triggerFadeOut
  }, rest)));
};
//# sourceMappingURL=ToastContainer.js.map